import type { BuildingType } from "components/BuildingProfile/types";

import { averageND } from "utils/dataManipulation";
import { dateStringToYear } from "utils/date";
import { createId } from "utils/general";

import type {
  BuildingLayerPreset,
  BuildingLayersFormValue,
  Coordinates,
  MixConfiguration,
  Point,
  PrefillFormData,
  SingleBuildingFormState,
} from "../model/types";

import { CUSTOM_KEY, LAYER_PRESET_ORDER, SUBLAYER_PRESET_KEYS } from "../components/layer-step/constants";
import { convertBuildingTypeArrayToFormData } from "./mixConfiguration";

const getLatLonFromOutline = (outline: Point[] | undefined): Coordinates => {
  if (!outline) return { lat: 0, lng: 0 };
  const center = averageND(outline);
  return { lat: center[0] ?? 0, lng: center[1] ?? 0 };
};

const getLatLonFromLocation = (location: PrefillFormData["location"] | undefined): Coordinates => {
  if (!location) return { lat: 0, lng: 0 };
  if (location.latitude && location.longitude) {
    return { lat: location.latitude, lng: location.longitude };
  }
  return getLatLonFromOutline(location.buildingPolygons);
};

const prefillLayerNameToForm = (layerName: string): { name: string; preset: BuildingLayerPreset } => {
  if (LAYER_PRESET_ORDER.includes(layerName as BuildingLayerPreset)) {
    return { name: "", preset: layerName as BuildingLayerPreset };
  }
  if (layerName === CUSTOM_KEY) {
    return { name: "", preset: CUSTOM_KEY };
  }
  return { name: layerName, preset: CUSTOM_KEY };
};

const convertPrefillLayersToFormData = (layers: PrefillFormData["layers"]): BuildingLayersFormValue => {
  if (!layers) return [];
  return layers.map((layer) => {
    const { name, preset } = prefillLayerNameToForm(layer.layerName);
    return {
      id: createId(),
      name,
      preset,
      subLayers: layer.sublayers?.map((sublayer) => {
        const subName = (sublayer.name ?? "").trim();
        const isPresetSublayerKey = subName ? SUBLAYER_PRESET_KEYS[preset].includes(subName) : false;
        return {
          id: createId(),
          name: subName,
          nameFromPreset: isPresetSublayerKey ? true : undefined,
          objects: sublayer.objects.map((object) => ({
            id: createId(),
            name: object.name,
            points: object.polygon,
          })),
        };
      }),
      visible: true,
    };
  });
};

export const convertPrefillBuildingTypeToFormData = (
  buildingType: PrefillFormData["spatialInformation"] | undefined
): {
  building_type: BuildingType | undefined;
  mixConfiguration: MixConfiguration | undefined;
} => {
  const buildingTypeArray = !buildingType
    ? []
    : Object.entries(buildingType).map(([key, value]) => ({
        percentage: value,
        type: key,
      }));
  return convertBuildingTypeArrayToFormData(buildingTypeArray);
};

export const convertPrefillFormDataToFormData = (
  prefill: PrefillFormData | undefined
): SingleBuildingFormState | undefined => {
  if (!prefill) return;

  const latLong = getLatLonFromLocation(prefill.location);
  const bulidingType = convertPrefillBuildingTypeToFormData(prefill.spatialInformation);
  return {
    addressInfo: {
      address: prefill.location.street,
      city: prefill.location.city,
      country: prefill.location.country,
      name: prefill.name,
      postal_code: prefill.location.postCode,
    },
    buildingCenter: latLong,
    details: {
      building_type: bulidingType.building_type,
      construction_year: prefill.building.constructionYear,
      mixConfiguration: bulidingType.mixConfiguration,
      no_of_floors: prefill.building.floorAmount,
      rental_units: prefill.building.rentalUnits,
    },
    extraDetails: {
      has_cooling: prefill.energy.coolingAvailable ?? false,
      has_pv: prefill.energy.pvSystemAvailable ?? false,
      heating_system: prefill.energy.heatingSystem ?? undefined,
      installation_date: dateStringToYear(prefill.energy.heatingSystemInstalledAt),
      mechanical_ventilation: prefill.energy.mechanicalVentilation ?? false,
      modernizationElements: {
        facade: {
          modernization_date: dateStringToYear(prefill.energy.modernizationFacadeAt),
          modernized: !!prefill.energy.modernizationFacadeAt,
        },
        rooftop: {
          modernization_date: dateStringToYear(prefill.energy.modernizationRooftopAt),
          modernized: !!prefill.energy.modernizationRooftopAt,
        },
        windows: {
          modernization_date: dateStringToYear(prefill.energy.modernizationWindowsAt),
          modernized: !!prefill.energy.modernizationWindowsAt,
        },
      },
      modernized: prefill.energy.hasModernizations,
    },
    files: undefined,
    images: [],
    layers: convertPrefillLayersToFormData(prefill.layers),
    outline: prefill.location.buildingPolygons ?? [],
  };
};
